import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { memo, useMemo } from 'react'
import { 
  LayoutDashboard, 
  BarChart3, 
  Users, 
  MessageSquare,
  Settings,
  Hash,
  FileText,
  Cog,
  MapPin,
  Ticket
} from 'lucide-react'

interface SidebarProps {
  collapsed?: boolean
}

const Sidebar = memo(function Sidebar({ collapsed = false }: SidebarProps) {
  const location = useLocation()
  const { t } = useTranslation()

  // 🎯 SIMPLIFIED: Clean navigation items - only essential info
  const navigation = useMemo(() => [
    {
      name: t('navigation.dashboard'),
      href: '/dashboard',
      icon: LayoutDashboard,
    },
    {
      name: t('navigation.channels'),
      href: '/channels',
      icon: Hash,
    },
    {
      name: t('navigation.categories'),
      href: '/category/answerQuality',
      icon: BarChart3,
    },
    {
      name: t('navigation.profiles'),
      href: '/profile',
      icon: Users,
    },
    {
      name: t('navigation.coaching'),
      href: '/coaching',
      icon: MessageSquare,
    },
    {
      name: t('navigation.reports'),
      href: '/reports',
      icon: FileText,
    },
    {
      name: t('navigation.configuration'),
      href: '/configuration',
      icon: Cog,
    },
    {
      name: t('navigation.teammap'),
      href: '/teammap',
      icon: MapPin,
    },
    {
      name: t('navigation.jira'),
      href: '/jira',
      icon: Ticket,
    },
    {
      name: t('navigation.settings'),
      href: '/settings',
      icon: Settings,
    },
  ], [t])

  // 🎯 CLEAN: Simple active check
  const isItemActive = useMemo(() => (href: string) => {
    return location.pathname.startsWith(href) || 
           (href.includes('/category') && location.pathname.includes('/category'))
  }, [location.pathname])

  return (
    <div className="flex flex-col h-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl">
      {/* Modern logo section */}
      <div className="flex items-center px-6 py-8 border-b border-gray-200/50 dark:border-gray-700/50">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-sm">fb</span>
          </div>
          
          {!collapsed && (
            <span className="text-xl font-bold text-gray-900 dark:text-white bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
              feedbeq
            </span>
          )}
        </div>
      </div>

      {/* Modern navigation */}
      <nav className="flex-1 px-5 py-8 space-y-3">
        {navigation.map((item) => {
          const isActive = isItemActive(item.href)
          const Icon = item.icon
          
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`nav-item ${
                isActive ? 'active' : ''
              }`}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!collapsed && (
                <span className="truncate font-medium">{item.name}</span>
              )}
            </Link>
          )
        })}
      </nav>

      {/* Modern footer */}
      {!collapsed && (
        <div className="px-6 py-6 border-t border-gray-200/50 dark:border-gray-700/50">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            feedbeq v2.0
          </p>
        </div>
      )}
    </div>
  )
})

export default Sidebar 